import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
  IconButton,
  Paper,
  Grid,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Chip,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
  Close as CloseIcon,
  Restore as RestoreIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { Formio } from 'formiojs';
import { getFormTemplates, createFormTemplate, updateFormTemplate, archiveFormTemplate } from '../services/api';
import { FormTemplate, ActionButton } from '../types/form';

// Import Formio CSS
import 'formiojs/dist/formio.full.min.css';

// Import Quill CSS for WYSIWYG textarea editor
import 'quill/dist/quill.snow.css';

// Register "Rich Text Editor" as a pre-configured textarea with Quill WYSIWYG.
// This appears as a distinct component in the builder sidebar so form designers
// don't need to manually configure the editor type. At render time, both the
// dashboard and iPart transform it back to a standard textarea with Quill —
// no custom component registration needed on the render side.
const registerBuilderComponents = () => {
  const FormioLib = Formio as any;
  if (!FormioLib?.Components?.components) return;
  if (FormioLib.Components.components.richtexteditor) return;

  const TextAreaComponent = FormioLib.Components.components.textarea;

  class RichTextEditorComponent extends TextAreaComponent {
    static schema(...extend: any[]) {
      return TextAreaComponent.schema({
        type: 'richtexteditor',
        label: 'Rich Text',
        key: 'richText',
        input: true,
        wysiwyg: true,
        editor: 'quill',
        isUploadEnabled: false,
        rows: 6
      }, ...extend);
    }

    static get builderInfo() {
      return {
        title: 'Rich Text Editor',
        icon: 'font',
        group: 'basic',
        weight: 15,
        documentation: 'Multi-line text with formatting toolbar (bold, italic, lists, etc.)',
        schema: RichTextEditorComponent.schema()
      };
    }

    get defaultSchema() {
      return RichTextEditorComponent.schema();
    }
  }

  FormioLib.Components.addComponent('richtexteditor', RichTextEditorComponent);

  // Register "File Upload" as a custom component for the builder sidebar.
  // Stored as type 'fileupload' in the schema. FormRenderer and iPart register
  // the interactive version of this component for actual file handling.
  const FieldComponent = FormioLib.Components.components.field;

  class FileUploadBuilderComponent extends FieldComponent {
    static schema(...extend: any[]) {
      return FieldComponent.schema({
        type: 'fileupload',
        label: 'File Upload',
        key: 'fileUpload',
        input: true,
        multiple: true
      }, ...extend);
    }

    static get builderInfo() {
      return {
        title: 'File Upload',
        icon: 'upload',
        group: 'advanced',
        weight: 5,
        documentation: 'Upload files (PDF, Word, images). Stored in S3 with signed download URLs.',
        schema: FileUploadBuilderComponent.schema()
      };
    }

    get defaultSchema() {
      return FileUploadBuilderComponent.schema();
    }
  }

  FormioLib.Components.addComponent('fileupload', FileUploadBuilderComponent);
};

registerBuilderComponents();

// Default action buttons for new forms
const DEFAULT_ACTION_BUTTONS: ActionButton[] = [
  { id: 'submit', label: 'Submit', value: 'submit', order: 1, color: 'primary' }
];

// Available colors for action buttons
const BUTTON_COLORS: Array<{ value: ActionButton['color']; label: string }> = [
  { value: 'primary', label: 'Primary (Blue)' },
  { value: 'secondary', label: 'Secondary (Gray)' },
  { value: 'success', label: 'Success (Green)' },
  { value: 'error', label: 'Error (Red)' },
  { value: 'warning', label: 'Warning (Orange)' },
  { value: 'info', label: 'Info (Light Blue)' }
];

// Global storage for sample payload (accessible by Formio edit forms)
declare global {
  interface Window {
    __formBuilderPayload?: any;
  }
}

// Helper to render payload tree as HTML
const renderPayloadTree = (obj: any, path: string = '', level: number = 0): string => {
  let html = '';
  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const value = obj[key];
    const fullPath = path ? `${path}.${key}` : key;
    const indent = level * 16;

    if (value === null || value === undefined) continue;

    if (Array.isArray(value)) {
      html += `<div style="margin-left:${indent}px;padding:4px 0;">
        <span style="color:#856404;font-weight:bold;">▸ ${key}</span>
        <span style="color:#6c757d;font-size:11px;"> [Array: ${value.length} items]</span>
        <button type="button" class="btn btn-sm btn-outline-primary ms-2 payload-key-btn"
          data-key="${fullPath}" style="padding:1px 6px;font-size:11px;">
          Use
        </button>
      </div>`;
      if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        html += `<div style="margin-left:${indent + 8}px;color:#6c757d;font-size:11px;font-style:italic;">First item structure:</div>`;
        // Use dot notation for array indices (e.g., items.0.name instead of items[0].name)
        // Formio keys don't support bracket notation
        html += renderPayloadTree(value[0], `${fullPath}.0`, level + 1);
      }
    } else if (typeof value === 'object' && value !== null) {
      html += `<div style="margin-left:${indent}px;padding:4px 0;">
        <span style="color:#0d6efd;font-weight:bold;">▸ ${key}</span>
        <span style="color:#6c757d;font-size:11px;"> {Object}</span>
        <button type="button" class="btn btn-sm btn-outline-primary ms-2 payload-key-btn"
          data-key="${fullPath}" style="padding:1px 6px;font-size:11px;">
          Use
        </button>
      </div>`;
      html += renderPayloadTree(value, fullPath, level + 1);
    } else {
      const preview = typeof value === 'string'
        ? `"${value.substring(0, 30)}${value.length > 30 ? '...' : ''}"`
        : String(value);
      html += `<div style="margin-left:${indent}px;padding:4px 0;">
        <span style="color:#198754;">${key}</span>
        <span style="color:#6c757d;font-size:11px;"> = ${preview}</span>
        <button type="button" class="btn btn-sm btn-outline-primary ms-2 payload-key-btn"
          data-key="${fullPath}" style="padding:1px 6px;font-size:11px;">
          Use
        </button>
      </div>`;
    }
  }
  return html;
};

// Inject payload tree into the Data Binding tab when editing a component
const injectPayloadTree = () => {
  const container = document.querySelector('.payload-tree-container');
  if (!container) return;

  const payload = window.__formBuilderPayload;

  if (!payload || Object.keys(payload).length === 0) {
    container.innerHTML = `
      <div class="alert alert-info" style="margin: 0;">
        <strong>No payload available.</strong><br>
        <small>Go to the "Form Settings" tab and add a sample JSON payload to enable data binding suggestions.</small>
      </div>
    `;
    return;
  }

  // Build the payload tree UI
  const treeHtml = renderPayloadTree(payload);
  container.innerHTML = `
    <div class="payload-key-picker" style="max-height: 300px; overflow-y: auto; border: 1px solid #dee2e6; border-radius: 4px; padding: 12px; background: #f8f9fa;">
      <div style="margin-bottom: 8px; font-weight: 500; color: #495057;">
        Click "Use" to set the Property Name:
      </div>
      ${treeHtml}
    </div>
  `;

  // Add click handlers for "Use" buttons
  container.querySelectorAll('.payload-key-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const key = (btn as HTMLElement).getAttribute('data-key');
      if (key) {
        setComponentKey(key);
      }
    });
  });
};

// Set the component's key (Property Name) from the payload tree
const setComponentKey = (key: string) => {
  // Find the key input in the API tab
  const keyInput = document.querySelector('[name="data[key]"]') as HTMLInputElement;
  if (keyInput) {
    keyInput.value = key;
    keyInput.dispatchEvent(new Event('input', { bubbles: true }));
    keyInput.dispatchEvent(new Event('change', { bubbles: true }));

    // Show success feedback
    const container = document.querySelector('.payload-tree-container');
    if (container) {
      const existing = container.querySelector('.alert-success');
      if (existing) existing.remove();

      const feedback = document.createElement('div');
      feedback.className = 'alert alert-success mt-2';
      feedback.style.cssText = 'padding: 8px 12px; font-size: 12px; margin-top: 8px;';
      feedback.innerHTML = `<strong>Done!</strong> Property Name set to: <code>${key}</code>`;
      container.appendChild(feedback);
      setTimeout(() => feedback.remove(), 3000);
    }
  } else {
    // Fallback: try to find any key-related input
    console.warn('Could not find key input [name="data[key]"]');
    alert(`Copy this key to the "Property Name" field in the API tab:\n\n${key}`);
  }
};

// Create custom editForm with Data Binding tab and hidden fields
const createEditFormOverrides = () => {
  const componentTypes = ['textfield', 'textarea', 'richtexteditor', 'fileupload', 'number', 'email', 'select', 'checkbox', 'radio', 'datetime', 'hidden', 'currency', 'phoneNumber', 'url', 'selectboxes', 'day', 'signature'];

  const overrides: any = {};

  componentTypes.forEach(type => {
    overrides[type] = [
      // Hide unwanted Display tab properties (keep customClass for styling)
      {
        key: 'display',
        components: [
          { key: 'description', ignore: true },
          { key: 'prefix', ignore: true },
          { key: 'suffix', ignore: true }
        ]
      },
      // Add Data Binding tab
      {
        key: 'databinding',
        label: 'Data Binding',
        weight: 5, // After Display, before Data
        components: [
          {
            type: 'htmlelement',
            tag: 'div',
            className: 'payload-tree-container',
            content: '<div id="payload-tree-placeholder">Loading payload structure...</div>',
            refreshOnChange: true
          },
          {
            type: 'htmlelement',
            tag: 'hr',
            content: ''
          },
          {
            type: 'htmlelement',
            tag: 'p',
            className: 'text-muted',
            content: '<small>The "Property Name" in the API tab determines which payload field maps to this component. Use dot notation for nested values (e.g., member.firstName).</small>'
          }
        ]
      }
    ];
  });

  return overrides;
};

const FormBuilder: React.FC = () => {
  const [forms, setForms] = useState<FormTemplate[]>([]);
  const [allForms, setAllForms] = useState<FormTemplate[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedForm, setSelectedForm] = useState<FormTemplate | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formMetadata, setFormMetadata] = useState({
    name: '',
    description: ''
  });
  const [currentTab, setCurrentTab] = useState(0);
  const [samplePayload, setSamplePayload] = useState<string>('');
  const [payloadError, setPayloadError] = useState<string | null>(null);

  // Action buttons state
  const [actionButtons, setActionButtons] = useState<ActionButton[]>([]);
  const [editingButton, setEditingButton] = useState<ActionButton | null>(null);
  const [isButtonDialogOpen, setIsButtonDialogOpen] = useState(false);

  // Permissions state
  const [allowedUsers, setAllowedUsers] = useState<string[]>([]);
  const [newUsername, setNewUsername] = useState('');

  // Formio builder state
  const [formioSchema, setFormioSchema] = useState<any>({ components: [] });
  const formioBuilderRef = useRef<HTMLDivElement>(null);
  const formioBuilderInstance = useRef<any>(null);

  // Track the initial schema to load into the builder
  const initialSchemaRef = useRef<any>({ components: [] });
  const [builderReady, setBuilderReady] = useState(false);

  // Load Formio CSS dynamically
  useEffect(() => {
    const cssId = 'formio-builder-css';
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';
      link.href = 'https://cdn.form.io/formiojs/formio.full.min.css';
      document.head.appendChild(link);
    }

    const bootstrapCssId = 'formio-bootstrap-css';
    if (!document.getElementById(bootstrapCssId)) {
      const link = document.createElement('link');
      link.id = bootstrapCssId;
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap.min.css';
      document.head.appendChild(link);
    }

    // Load jQuery first (required for Bootstrap 4), then Bootstrap
    const jqueryId = 'formio-jquery-js';
    const bootstrapJsId = 'formio-bootstrap-js';

    const loadBootstrap = () => {
      if (!document.getElementById(bootstrapJsId)) {
        const bootstrapScript = document.createElement('script');
        bootstrapScript.id = bootstrapJsId;
        bootstrapScript.src = 'https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/js/bootstrap.bundle.min.js';
        document.body.appendChild(bootstrapScript);
      }
    };

    // Check if jQuery is already available globally
    if ((window as any).jQuery) {
      loadBootstrap();
    } else if (!document.getElementById(jqueryId)) {
      const jqueryScript = document.createElement('script');
      jqueryScript.id = jqueryId;
      jqueryScript.src = 'https://cdn.jsdelivr.net/npm/jquery@3.6.0/dist/jquery.min.js';
      jqueryScript.onload = loadBootstrap;
      document.body.appendChild(jqueryScript);
    }

    // Add custom CSS to fix z-index issues with Formio modals and dropdowns
    const customCssId = 'formio-custom-css';
    if (!document.getElementById(customCssId)) {
      const style = document.createElement('style');
      style.id = customCssId;
      style.textContent = `
        /* Fix Formio edit modal z-index to appear above MUI Dialog */
        .formio-dialog {
          z-index: 1400 !important;
        }
        .formio-dialog-overlay {
          z-index: 1399 !important;
          pointer-events: none !important;
        }
        /* Fix Choices.js dropdown z-index */
        .choices__list--dropdown {
          z-index: 1500 !important;
        }
        /* Fix Bootstrap modal backdrop */
        .modal-backdrop {
          z-index: 1398 !important;
        }
        /* Fix Bootstrap modal and its content */
        .modal {
          z-index: 1400 !important;
          pointer-events: auto !important;
        }
        .modal-dialog {
          z-index: 1401 !important;
          pointer-events: auto !important;
        }
        .modal-content {
          z-index: 1402 !important;
          pointer-events: auto !important;
        }
        .modal-footer {
          z-index: 1403 !important;
          pointer-events: auto !important;
        }
        .modal-footer button,
        .modal-footer .btn {
          z-index: 1404 !important;
          pointer-events: auto !important;
          position: relative;
        }
        /* Fix any select dropdowns */
        .formio-component-select .choices,
        .formio-component-select .choices__list--dropdown,
        .formio-component select {
          z-index: 1500 !important;
        }
        /* Ensure Choices.js dropdown items are clickable */
        .choices__list--dropdown .choices__item {
          pointer-events: auto !important;
          cursor: pointer !important;
        }
        .choices__list--dropdown {
          pointer-events: auto !important;
        }
        /* Ensure Formio dialog content is interactive */
        .formio-dialog-content {
          pointer-events: auto !important;
        }
        .formio-dialog-content * {
          pointer-events: auto !important;
        }
      `;
      document.head.appendChild(style);
    }

    // Set up a MutationObserver to detect when the Data Binding tab is shown
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          // Check if the Data Binding tab content just became visible
          const container = document.querySelector('.payload-tree-container');
          const picker = container?.querySelector('.payload-key-picker');
          // If container exists but hasn't been populated yet
          if (container && !picker && container.innerHTML.includes('payload-tree-placeholder')) {
            injectPayloadTree();
          }
        }
      });
    });

    // Observe the body for changes (edit modals are added dynamically)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    // Also listen for tab clicks in the Formio edit modal
    const handleTabClick = (e: Event) => {
      const target = e.target as HTMLElement;
      // Check if clicking on a nav tab in the edit form
      if (target.closest('.nav-link') && target.textContent?.includes('Data Binding')) {
        setTimeout(() => injectPayloadTree(), 50);
      }
    };
    document.addEventListener('click', handleTabClick);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', handleTabClick);
    };
  }, []);

  // Update global payload when it changes
  useEffect(() => {
    try {
      window.__formBuilderPayload = samplePayload.trim() ? JSON.parse(samplePayload) : null;
    } catch {
      window.__formBuilderPayload = null;
    }
  }, [samplePayload]);

  // Track when the builder container is ready (Tab 1 = Form Designer)
  useEffect(() => {
    if (isDialogOpen && currentTab === 1) {
      const timer = setTimeout(() => {
        setBuilderReady(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Destroy builder when switching away from the designer tab,
      // since React unmounts the container div. The builder will be
      // re-created with the current schema when switching back.
      if (formioBuilderInstance.current) {
        // Save current schema before destroying
        const currentSchema = formioBuilderInstance.current.schema;
        if (currentSchema) {
          initialSchemaRef.current = currentSchema;
        }
        formioBuilderInstance.current.destroy();
        formioBuilderInstance.current = null;
      }
      setBuilderReady(false);
    }
  }, [isDialogOpen, currentTab]);

  // Initialize/destroy Formio builder when ready
  useEffect(() => {
    if (builderReady && formioBuilderRef.current && !formioBuilderInstance.current) {
      Formio.builder(formioBuilderRef.current, initialSchemaRef.current, {
        builder: {
          basic: {
            title: 'Basic',
            default: true,
            weight: 0,
            components: {
              textfield: true,
              textarea: true,
              richtexteditor: true,
              number: true,
              checkbox: true,
              selectboxes: true,
              select: true,
              radio: true,
              password: false
            }
          },
          advanced: {
            title: 'Advanced',
            weight: 10,
            components: {
              email: true,
              url: true,
              phoneNumber: true,
              datetime: true,
              day: true,
              fileupload: true,
              file: false,  // Replaced by custom fileupload component
              button: false,
              currency: false,
              signature: false,
              tags: false,
              address: false,
              time: false,
              survey: false
            }
          },
          layout: {
            title: 'Layout',
            weight: 20,
            components: {
              htmlelement: true,
              content: false,
              columns: true,
              fieldset: true,
              panel: true,
              table: true,
              tabs: true,
              well: true
            }
          },
          data: {
            title: 'Data',
            weight: 30,
            components: {
              hidden: true,
              container: true,
              datagrid: true,
              editgrid: true
            }
          },
          premium: {
            title: 'Premium',
            weight: 40,
            components: {
              form: false,
              custom: false
            }
          }
        },
        editForm: createEditFormOverrides(),
        noDefaultSubmitButton: true
      }).then((builder: any) => {
        formioBuilderInstance.current = builder;

        builder.on('change', (schema: any) => {
          setFormioSchema(schema);
        });

        // Inject payload tree when editing a component
        builder.on('editComponent', (component: any) => {
          // Wait for the edit modal to render
          setTimeout(() => {
            injectPayloadTree();
          }, 100);
        });
      }).catch((err: any) => {
        console.error('Failed to initialize Formio builder:', err);
      });
    }

    // Cleanup when dialog closes
    if (!isDialogOpen && formioBuilderInstance.current) {
      formioBuilderInstance.current.destroy();
      formioBuilderInstance.current = null;
    }
  }, [builderReady, isDialogOpen]);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await getFormTemplates();
      const templates = response.data.templates || [];
      setAllForms(templates);
      // Filter based on showArchived setting
      const filteredForms = showArchived
        ? templates
        : templates.filter((form: FormTemplate) => form.status !== 'archived');
      setForms(filteredForms);
      setError(null);
    } catch (err: any) {
      setError('Failed to fetch forms: ' + (err.response?.data?.error || err.message));
    }
  };

  // Update displayed forms when showArchived changes
  useEffect(() => {
    const filteredForms = showArchived
      ? allForms
      : allForms.filter((form: FormTemplate) => form.status !== 'archived');
    setForms(filteredForms);
  }, [showArchived, allForms]);

  const handleCreateNew = () => {
    setSelectedForm(null);
    setFormMetadata({
      name: '',
      description: ''
    });
    const emptySchema = { components: [] };
    setFormioSchema(emptySchema);
    initialSchemaRef.current = emptySchema;
    setActionButtons([...DEFAULT_ACTION_BUTTONS]); // Set default action buttons
    setAllowedUsers([]);
    setNewUsername('');
    setSamplePayload('');
    setPayloadError(null);
    setCurrentTab(0); // Start on Form Settings
    setIsDialogOpen(true);
  };

  const handleEdit = (form: FormTemplate) => {
    setSelectedForm(form);
    setFormMetadata({
      name: form.name,
      description: form.description || ''
    });

    // Load schema directly for Formio builder
    const schemaForBuilder = form.schema?.components
      ? { ...form.schema }
      : { components: [] };

    setFormioSchema(schemaForBuilder);
    initialSchemaRef.current = schemaForBuilder;

    // Load action buttons from schema
    if (form.schema?.actionButtons && form.schema.actionButtons.length > 0) {
      setActionButtons(form.schema.actionButtons);
    } else {
      setActionButtons([]);
    }

    // Load allowed users
    setAllowedUsers(form.allowedUsers || []);
    setNewUsername('');

    // Load sample payload from metadata
    if (form.metadata?.samplePayload) {
      setSamplePayload(JSON.stringify(form.metadata.samplePayload, null, 2));
    } else {
      setSamplePayload('');
    }
    setPayloadError(null);

    setCurrentTab(0); // Start on Form Settings
    setIsDialogOpen(true);
  };

  const handleDelete = async (formId: string, currentStatus: string) => {
    const action = currentStatus === 'archived' ? 'permanently delete' : 'archive';
    if (!window.confirm(`Are you sure you want to ${action} this form?`)) return;

    try {
      if (currentStatus === 'archived') {
        setError('Permanent deletion not implemented. Forms can only be archived.');
        return;
      }

      await archiveFormTemplate(formId);
      setSuccess('Form archived successfully');
      fetchForms();
    } catch (err: any) {
      setError('Failed to archive form: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRestore = async (formId: string) => {
    if (!window.confirm('Are you sure you want to restore this form?')) return;

    try {
      await updateFormTemplate(formId, { status: 'draft' });
      setSuccess('Form restored successfully');
      fetchForms();
    } catch (err: any) {
      setError('Failed to restore form: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSave = async () => {
    if (!formMetadata.name) {
      setError('Please provide a form name');
      return;
    }

    // Parse and validate sample payload if provided
    let parsedPayload = null;
    if (samplePayload.trim()) {
      try {
        parsedPayload = JSON.parse(samplePayload);
        setPayloadError(null);
      } catch (err) {
        setError('Invalid JSON in sample payload. Please fix the JSON syntax.');
        setPayloadError('Invalid JSON syntax');
        setCurrentTab(0); // Switch to Form Settings to show the error
        return;
      }
    }

    // Use formioSchema directly (from the visual builder)
    // Include action buttons in the schema
    const schema = {
      ...formioSchema,
      actionButtons: actionButtons.length > 0 ? actionButtons : undefined
    };

    try {
      const payload = {
        ...formMetadata,
        schema,
        allowedUsers: allowedUsers.length > 0 ? allowedUsers : null,
        metadata: {
          ...(parsedPayload ? { samplePayload: parsedPayload } : {})
        }
      };

      if (selectedForm) {
        await updateFormTemplate(selectedForm.id, payload);
        setSuccess('Form updated successfully');
      } else {
        await createFormTemplate(payload);
        setSuccess('Form created successfully');
      }

      setIsDialogOpen(false);
      fetchForms();
    } catch (err: any) {
      setError('Failed to save form: ' + (err.response?.data?.error || err.message));
    }
  };

  const handlePreview = (form: FormTemplate) => {
    setSelectedForm(form);
    setIsPreviewOpen(true);
  };

  // Render preview form using Formio
  const previewFormRef = useRef<HTMLDivElement>(null);
  const previewFormInstance = useRef<any>(null);

  useEffect(() => {
    if (!isPreviewOpen || !selectedForm?.schema) {
      return;
    }

    // Small delay to ensure the dialog has rendered and ref is available
    const timer = setTimeout(() => {
      if (!previewFormRef.current) {
        console.error('Preview form ref still not available');
        return;
      }

      // Destroy existing preview form
      if (previewFormInstance.current) {
        previewFormInstance.current.destroy();
        previewFormInstance.current = null;
      }

      // Create new preview form
      Formio.createForm(previewFormRef.current, selectedForm.schema, {
        readOnly: false
      }).then((form: any) => {
        previewFormInstance.current = form;

        // Listen for custom events (workflow buttons)
        form.on('customEvent', (event: any) => {
          console.log('Custom event:', event);
          if (event.type === 'cancel') {
            // Cancel just closes the form without doing anything
            setIsPreviewOpen(false);
          } else {
            alert(`Workflow action triggered: ${event.type}\n\nIn production, this would submit to n8n with action: "${event.type}"`);
          }
        });

        // Pre-fill with sample payload if available
        if (selectedForm.metadata?.samplePayload) {
          form.submission = { data: selectedForm.metadata.samplePayload };
        }
      }).catch((err: any) => {
        console.error('Failed to create preview form:', err);
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      if (previewFormInstance.current) {
        previewFormInstance.current.destroy();
        previewFormInstance.current = null;
      }
    };
  }, [isPreviewOpen, selectedForm]);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Form Builder</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
            }
            label="Show archived"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Create New Form
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {forms.map((form) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={form.id}>
            <Card sx={{ opacity: form.status === 'archived' ? 0.7 : 1 }}>
              <CardContent>
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">{form.name}</Typography>
                  {form.status === 'archived' && (
                    <Chip label="Archived" size="small" color="default" />
                  )}
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {form.description || 'No description'}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    {form.schema?.components?.length || 0} components
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <IconButton
                    size="small"
                    onClick={() => handlePreview(form)}
                    title="Preview"
                  >
                    <PreviewIcon />
                  </IconButton>
                  {form.status === 'archived' ? (
                    <IconButton
                      size="small"
                      onClick={() => handleRestore(form.id)}
                      title="Restore Form"
                      color="primary"
                    >
                      <RestoreIcon />
                    </IconButton>
                  ) : (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(form)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(form.id, form.status)}
                        title="Archive Form"
                        color="warning"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Form Builder Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth={false}
        fullWidth
        disableEnforceFocus
        hideBackdrop
        disableScrollLock
        sx={{
          '& .MuiDialog-container': {
            pointerEvents: 'none'
          },
          '& .MuiPaper-root': {
            pointerEvents: 'auto'
          }
        }}
        PaperProps={{
          sx: {
            width: '95vw',
            maxWidth: '1400px',
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedForm ? 'Edit Form' : 'Create New Form'}
          <IconButton onClick={() => setIsDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => setCurrentTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
          >
            <Tab label="Form Settings" />
            <Tab label="Form Designer" />
            <Tab label="Action Buttons" />
            <Tab label="Permissions" />
          </Tabs>

          {/* Tab 0: Form Settings */}
          {currentTab === 0 && (
            <Box sx={{ mt: 2, overflow: 'auto', flex: 1 }}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField
                    label="Form Name"
                    fullWidth
                    required
                    value={formMetadata.name}
                    onChange={(e) => setFormMetadata({ ...formMetadata, name: e.target.value })}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    label="Description"
                    fullWidth
                    value={formMetadata.description}
                    onChange={(e) => setFormMetadata({ ...formMetadata, description: e.target.value })}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="Sample Payload (JSON)"
                    fullWidth
                    multiline
                    rows={12}
                    value={samplePayload}
                    onChange={(e) => {
                      setSamplePayload(e.target.value);
                      setPayloadError(null);
                    }}
                    placeholder='Paste a sample JSON payload here. This data will be used to:
1. Pre-fill form fields when rendering the form
2. Provide a "Data Binding" tab in the form designer to easily map payload keys to form fields

Example:
{
  "member": {
    "firstName": "John",
    "lastName": "Doe",
    "email": "john@example.com"
  },
  "requestType": "membership",
  "status": "pending"
}'
                    helperText={payloadError || "This sample payload will be available in the Form Designer's 'Data Binding' tab for each field"}
                    error={!!payloadError}
                    InputProps={{
                      sx: { fontFamily: 'monospace', fontSize: '12px' }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 1: Form Designer - Formio Visual Builder */}
          {currentTab === 1 && (
            <Box sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2, flexShrink: 0 }}>
                Drag components from the left panel to build your form.
                Click any component and go to the <strong>Data Binding</strong> tab to map payload fields.
              </Typography>
              <Paper
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  '& .formio-builder': {
                    minHeight: '100%'
                  }
                }}
              >
                <div ref={formioBuilderRef} style={{ minHeight: '100%' }} />
              </Paper>
            </Box>
          )}

          {/* Tab 2: Action Buttons */}
          {currentTab === 2 && (
            <Box sx={{ mt: 2, overflow: 'auto', flex: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Configure the action buttons that appear at the bottom of the form dialog.
                Each button triggers a different workflow action when clicked.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Fixed buttons:</strong> Save &amp; Close and Cancel are always present.
                They save drafts or close the form without triggering workflow actions.
              </Alert>

              {/* Action Buttons List */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Workflow Action Buttons
                </Typography>

                {actionButtons.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    No action buttons configured. Add at least one button to allow form submission.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {actionButtons
                      .sort((a, b) => a.order - b.order)
                      .map((btn, index) => (
                        <Paper key={btn.id} variant="outlined" sx={{ p: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                              variant="contained"
                              color={btn.color}
                              size="small"
                              sx={{ minWidth: 100, pointerEvents: 'none' }}
                            >
                              {btn.label}
                            </Button>
                            <Typography variant="body2" color="textSecondary" sx={{ flex: 1 }}>
                              Value: <code>{btn.value}</code>
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const sorted = [...actionButtons].sort((a, b) => a.order - b.order);
                                  if (index > 0) {
                                    const newButtons = [...sorted];
                                    const temp = newButtons[index].order;
                                    newButtons[index].order = newButtons[index - 1].order;
                                    newButtons[index - 1].order = temp;
                                    setActionButtons(newButtons);
                                  }
                                }}
                                disabled={index === 0}
                                title="Move Up"
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const sorted = [...actionButtons].sort((a, b) => a.order - b.order);
                                  if (index < sorted.length - 1) {
                                    const newButtons = [...sorted];
                                    const temp = newButtons[index].order;
                                    newButtons[index].order = newButtons[index + 1].order;
                                    newButtons[index + 1].order = temp;
                                    setActionButtons(newButtons);
                                  }
                                }}
                                disabled={index === actionButtons.length - 1}
                                title="Move Down"
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingButton(btn);
                                  setIsButtonDialogOpen(true);
                                }}
                                title="Edit"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setActionButtons(actionButtons.filter(b => b.id !== btn.id));
                                }}
                                title="Delete"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                  </Box>
                )}

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const maxOrder = actionButtons.length > 0
                      ? Math.max(...actionButtons.map(b => b.order))
                      : 0;
                    setEditingButton({
                      id: `action_${Date.now()}`,
                      label: '',
                      value: '',
                      order: maxOrder + 1,
                      color: 'primary'
                    });
                    setIsButtonDialogOpen(true);
                  }}
                >
                  Add Action Button
                </Button>
              </Paper>

              {/* Button Preview */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Button Bar Preview
                </Typography>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1
                }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" color="secondary" size="small">
                      Save &amp; Close
                    </Button>
                    <Button variant="outlined" color="secondary" size="small">
                      Cancel
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {actionButtons
                      .sort((a, b) => a.order - b.order)
                      .map(btn => (
                        <Button
                          key={btn.id}
                          variant="contained"
                          color={btn.color}
                          size="small"
                        >
                          {btn.label || '(No Label)'}
                        </Button>
                      ))}
                    {actionButtons.length === 0 && (
                      <Typography variant="body2" color="textSecondary">
                        (No action buttons)
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Tab 3: Permissions */}
          {currentTab === 3 && (
            <Box sx={{ mt: 2, overflow: 'auto', flex: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Restrict which iMIS users can see and claim tasks using this form.
                Leave empty to allow all users.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  label="iMIS Login Name"
                  size="small"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const trimmed = newUsername.trim();
                      if (trimmed && !allowedUsers.includes(trimmed)) {
                        setAllowedUsers([...allowedUsers, trimmed]);
                        setNewUsername('');
                      }
                    }
                  }}
                  placeholder="e.g. jsmith"
                  sx={{ flexGrow: 1, maxWidth: 400 }}
                />
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => {
                    const trimmed = newUsername.trim();
                    if (trimmed && !allowedUsers.includes(trimmed)) {
                      setAllowedUsers([...allowedUsers, trimmed]);
                      setNewUsername('');
                    }
                  }}
                  disabled={!newUsername.trim()}
                >
                  Add User
                </Button>
              </Box>

              {allowedUsers.length > 0 ? (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Allowed Users ({allowedUsers.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {allowedUsers.map((user) => (
                      <Chip
                        key={user}
                        label={user}
                        onDelete={() => setAllowedUsers(allowedUsers.filter((u) => u !== user))}
                      />
                    ))}
                  </Box>
                </Paper>
              ) : (
                <Alert severity="info">
                  No user restrictions — all authenticated users can access tasks using this form.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
            Save Form
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} maxWidth="md" fullWidth disableEnforceFocus>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Preview: {selectedForm?.name}</Typography>
            <IconButton onClick={() => setIsPreviewOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            This preview shows how the form will render. Action buttons are shown below the form.
            {selectedForm?.metadata?.samplePayload && ' Form is pre-filled with sample payload data.'}
          </Typography>
          <Paper sx={{ p: 2 }}>
            <div ref={previewFormRef} />
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" color="secondary" onClick={() => alert('Save & Close: Would save draft and close')}>
              Save &amp; Close
            </Button>
            <Button variant="outlined" color="secondary" onClick={() => setIsPreviewOpen(false)}>
              Cancel
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {selectedForm?.schema?.actionButtons
              ?.sort((a: ActionButton, b: ActionButton) => a.order - b.order)
              .map((btn: ActionButton) => (
                <Button
                  key={btn.id}
                  variant="contained"
                  color={btn.color}
                  onClick={() => alert(`Action triggered: ${btn.value}\n\nIn production, this would submit the form with selectedAction="${btn.value}"`)}
                >
                  {btn.label}
                </Button>
              ))}
            {(!selectedForm?.schema?.actionButtons || selectedForm.schema.actionButtons.length === 0) && (
              <Typography variant="body2" color="textSecondary" sx={{ alignSelf: 'center' }}>
                (No action buttons configured)
              </Typography>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Action Button Edit Dialog */}
      <Dialog
        open={isButtonDialogOpen}
        onClose={() => {
          setIsButtonDialogOpen(false);
          setEditingButton(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingButton?.label ? 'Edit Action Button' : 'Add Action Button'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Button Label"
              fullWidth
              required
              value={editingButton?.label || ''}
              onChange={(e) => setEditingButton(prev => prev ? { ...prev, label: e.target.value } : null)}
              placeholder="e.g., Submit, Approve, Reject"
              helperText="The text displayed on the button"
            />
            <TextField
              label="Action Value"
              fullWidth
              required
              value={editingButton?.value || ''}
              onChange={(e) => setEditingButton(prev => prev ? { ...prev, value: e.target.value } : null)}
              placeholder="e.g., submit, approve, reject"
              helperText="The value sent to n8n when this button is clicked (used for workflow branching)"
            />
            <TextField
              select
              label="Button Color"
              fullWidth
              value={editingButton?.color || 'primary'}
              onChange={(e) => setEditingButton(prev => prev ? { ...prev, color: e.target.value as ActionButton['color'] } : null)}
              helperText="Choose a color that reflects the button's purpose"
            >
              {BUTTON_COLORS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: 0.5,
                        bgcolor: `${option.value}.main`
                      }}
                    />
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            {/* Preview */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview:</Typography>
              <Button
                variant="contained"
                color={editingButton?.color || 'primary'}
              >
                {editingButton?.label || '(Enter a label)'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsButtonDialogOpen(false);
            setEditingButton(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (editingButton && editingButton.label && editingButton.value) {
                const existingIndex = actionButtons.findIndex(b => b.id === editingButton.id);
                if (existingIndex >= 0) {
                  // Update existing
                  const newButtons = [...actionButtons];
                  newButtons[existingIndex] = editingButton;
                  setActionButtons(newButtons);
                } else {
                  // Add new
                  setActionButtons([...actionButtons, editingButton]);
                }
                setIsButtonDialogOpen(false);
                setEditingButton(null);
              }
            }}
            disabled={!editingButton?.label || !editingButton?.value}
          >
            {actionButtons.find(b => b.id === editingButton?.id) ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormBuilder;
